import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useTranslation } from 'react-i18next';
import { useCreateFramework, useUpdateFramework } from '@/hooks/usePartnerships';
import type { Framework } from '../../../../shared/types/partnerships';
import { toLines, fromLines } from '../../../../shared/text-lines';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partnerId: string;
  framework?: Framework;
}

const STATUSES = ['PUBLISHED', 'DRAFT', 'ARCHIVED'] as const;

interface FormData {
  name: string; description: string; methodology: string;
  useCases: string; examples: string; driveUrl: string; status: string;
}


export function FrameworkFormDialog({ open, onOpenChange, partnerId, framework }: Props) {
  const { t } = useTranslation();
  const isEditing = !!framework;
  const create = useCreateFramework();
  const update = useUpdateFramework();

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<FormData>();
  const statusValue = watch('status');

  useEffect(() => {
    if (open) {
      reset({
        name: framework?.name || '', description: framework?.description || '',
        methodology: framework?.methodology || '',
        useCases: toLines(framework?.useCases),
        examples: toLines(framework?.examples),
        driveUrl: framework?.driveUrl || '',
        status: framework?.status || 'PUBLISHED',
      });
    }
  }, [open, framework, reset]);

  const onSubmit = async (data: FormData) => {
    const payload = {
      name: data.name, description: data.description,
      methodology: data.methodology || undefined,
      useCases: fromLines(data.useCases),
      examples: fromLines(data.examples),
      driveUrl: data.driveUrl || undefined,
      status: data.status as any,
    };

    if (isEditing) {
      await update.mutateAsync({ frameworkId: framework!.frameworkId, ...payload, expectedUpdatedAt: framework!.updatedAt });
    } else {
      await create.mutateAsync({ partnerId, ...payload });
    }
    onOpenChange(false);
  };

  const isBusy = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? t('partnerships.framework_form.title_edit') : t('partnerships.framework_form.title_create')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

          <div className="space-y-1.5">
            <Label>{t('partnerships.common.name_required')}</Label>
            <Input {...register('name', { required: true })} placeholder={t('partnerships.framework_form.como_vender_tiktok_en_lanzamientos')} />
            {errors.name && <p className="text-xs text-destructive">{t('partnerships.common.required_field')}</p>}
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.common.description_label')}</Label>
            <Textarea {...register('description')} rows={3} />
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.framework_form.methodology_label')}</Label>
            <Textarea {...register('methodology')} rows={3}
              placeholder={t('partnerships.framework_form.describe_el_marco_metodologico_paso_a')} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('partnerships.framework_form.use_cases_label')}</Label>
              <Textarea {...register('useCases')} rows={4}
                placeholder={t('partnerships.framework_form.lanzamiento_de_producto_10_brand_awareness')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('partnerships.framework_form.examples_label')}</Label>
              <Textarea {...register('examples')} rows={4} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.framework_form.drive_link_label')}</Label>
            <Input {...register('driveUrl')} type="url" placeholder="https://drive.google.com/..." />
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.common.status_label')}</Label>
            <Select value={statusValue} onValueChange={v => setValue('status', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common.ui.cancel')}</Button>
            <Button type="submit" disabled={isBusy}>
              {isBusy ? t('partnerships.common.saving') : isEditing ? t('partnerships.common.save_changes') : t('partnerships.framework_form.create_button')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
